import { skipToken } from '@reduxjs/toolkit/query';
import { isEmployeePriced } from 'pages/pricelists/modal/employeePricing';
import { hasStagedPricing } from 'pages/pricelists/modal/matterPricelistEditorUtils';
import { useGetContractByIdQuery } from 'store/api/contracts';
import { FeatureFlag } from 'types/features';
import { PricelistTabDraft } from 'types/matters';
import { MatterFollowedPricelist, PricingKind, ScopeType } from 'types/pricelists';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';

interface Staged {
    isCreate: boolean;
    pricingKind: PricingKind | undefined;
    followsPricelist: MatterFollowedPricelist | null | undefined;
    pricelists: PricelistTabDraft[];
    serviceAgreementContractId: number | undefined;
}

/**
 * The kind and the link the case form's price list stands for. A new case takes the kind of its service
 * agreement's list, else the tenant's mode; while nothing is picked it follows that agreement's list per employee,
 * as the create does when it carries no prices. An agreement chosen on a stored case changes neither.
 */
export function useStagedPricingLink({
    isCreate,
    pricingKind,
    followsPricelist,
    pricelists,
    serviceAgreementContractId,
}: Staged): { pricingKind: PricingKind; followsPricelist: MatterFollowedPricelist | null } {
    const isEmployeePricingEnabled = useIsFeatureEnabled(FeatureFlag.EMPLOYEE_PRICING);
    // The same read the form's agreement prefill makes, so it is served from that cache entry.
    const { data: agreement } = useGetContractByIdQuery(
        isCreate && serviceAgreementContractId != null ? String(serviceAgreementContractId) : skipToken
    );
    const agreementPricelist =
        agreement && agreement.id === serviceAgreementContractId && agreement.pricelist?.id
            ? agreement.pricelist
            : null;
    const tenantKind = isEmployeePricingEnabled ? PricingKind.EMPLOYEE : PricingKind.ROLE;
    const kind =
        pricingKind ?? (agreementPricelist ? (agreementPricelist.pricingKind ?? PricingKind.ROLE) : tenantKind);
    if (followsPricelist !== undefined) {
        return { pricingKind: kind, followsPricelist };
    }
    const followsAgreement =
        agreement && agreementPricelist && isEmployeePriced(agreementPricelist) && !hasStagedPricing(pricelists);
    return {
        pricingKind: kind,
        followsPricelist: followsAgreement
            ? {
                  id: agreementPricelist.id,
                  name: agreementPricelist.name,
                  scopeTypeCode: ScopeType.CONTRACT,
                  contractId: agreement.id,
                  modelPricelist: null,
              }
            : null,
    };
}
